import { requestCartRefresh } from './refreshCoordinator';
import { useCartStore } from '../store/useCartStore';
import { GlobalErrorHandler } from '../errors';

type CartRefreshTriggersOptions = {
  minIntervalMs?: number;
};

let cleanupRef: (() => void) | null = null;

export function initCartRefreshTriggers(options: CartRefreshTriggersOptions = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => undefined;
  }

  if (cleanupRef) return cleanupRef;

  const minIntervalMs = options.minIntervalMs ?? 2000;
  let lastRevalidateAt = 0;

  const tryRevalidate = async (reason: string) => {
    const now = Date.now();
    if (now - lastRevalidateAt < minIntervalMs) return;

    const state = useCartStore.getState();
    if (state.cartSyncStatus === 'syncing') return;

    lastRevalidateAt = now;

    try {
      const res = await requestCartRefresh(reason, { showLoading: false });
      if (!res.ok) {
        GlobalErrorHandler.handle(res.error, {
          scope: 'refresh',
          silent: true,
          userInitiated: false,
          source: 'fetch'
        });
      }
    } catch (err) {
      GlobalErrorHandler.handle(err, {
        scope: 'refresh',
        silent: true,
        userInitiated: false,
        source: 'fetch'
      });
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void tryRevalidate('trigger:visibility');
    }
  };

  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted || document.visibilityState === 'visible') {
      void tryRevalidate('trigger:pageshow');
    }
  };

  const onFocus = () => {
    void tryRevalidate('trigger:focus');
  };

  const onOnline = () => {
    void tryRevalidate('trigger:online');
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('focus', onFocus);
  window.addEventListener('online', onOnline);

  cleanupRef = () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('online', onOnline);
    cleanupRef = null;
  };

  return cleanupRef;
}
